export type InlineSegment = string | { bold: string };

export type ContentBlock =
  | { type: 'paragraph'; text: string | InlineSegment[] }
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'divider' };

export interface DocumentContent {
  title: string;
  lastUpdated: string;
  blocks: ContentBlock[];
}

const communityGuidelinesEn: DocumentContent = {
  title: 'TogetherWe Community Hub Participation Guidelines',
  lastUpdated: 'May 26, 2026',
  blocks: [
    {
      type: 'paragraph',
      text: "Welcome! The TogetherWe Community Hub is a shared community space designed to help people across Blaine County make connections, find support, and participate in what's happening around them through everyday community life.",
    },
    {
      type: 'paragraph',
      text: 'These guidelines outline what it means to participate with care, respect, and the good of community building.',
    },
    { type: 'heading', level: 2, text: '1. Be Respectful' },
    {
      type: 'paragraph',
      text: 'Treat others with kindness and curiosity, even when perspectives differ. Speak to neighbors the same way you would if you were meeting face-to-face in the community. Harassment, discrimination, bullying, or intimidation do not belong here.',
    },
    { type: 'heading', level: 2, text: '2. Be Real' },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub works best when people participate honestly. We ask everyone to use their real identity, represent themselves or their organization accurately, and share information they believe to be reliable. Trust grows when people know who they are connecting with.',
    },
    { type: 'heading', level: 2, text: '3. Be Community-Minded' },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub is created by community and for community and exists to support participation—not promotion alone. Postings should be related to Mental Well-Being. Share opportunities, invite collaboration, and contribute in ways that help others feel included and informed. Groups, conversations, and events should strengthen connection across the community.',
    },
    { type: 'heading', level: 2, text: '4. Help Keep This a Safe Space' },
    {
      type: 'paragraph',
      text: "Respect personal boundaries and protect the privacy of others. Avoid sharing sensitive personal information or posting about someone else’s experience without permission. If something feels concerning, Flag it so we can help keep the space welcoming for everyone.",
    },
    { type: 'heading', level: 2, text: '5. Support Youth Participation Responsibly' },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub welcomes participation from teens ages 13 and older. Adults should keep interactions public, appropriate, and respectful, and should never request personal contact information or move conversations with minors off-platform.',
    },
    { type: 'heading', level: 2, text: '6. Mental Health Conversations Matter Here' },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub supports connection and shared experience, but it is not a clinical service platform. Community members should avoid presenting themselves as licensed providers unless verified. If someone appears to be in crisis, encourage them to contact professional support such as 988 or emergency services.',
    },
    { type: 'heading', level: 2, text: '7. Groups Are Where Community Happens' },
    {
      type: 'paragraph',
      text: 'Groups are the heart of the TogetherWe Community Hub . Group leaders help create welcoming environments by keeping conversations respectful, purposes clear, and expectations transparent. Groups should reflect the same spirit of inclusion and trust as the larger platform. ',
    },
   { type: 'heading', level: 2, text: '8. If Something Feels Off, Say Something' },
    {
      type: 'paragraph',
      text: 'Community members help keep the Community Hub safe and welcoming by reporting harmful behavior, misinformation, or safety concerns. When needed, TogetherWe may remove posts or accounts that do not follow these guidelines.',
    },

  
    { type: 'heading', level: 2, text: "9. What Is Not Allowed on TogetherWe's Community Hub" },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub is designed to be a welcoming space for connection and participation across Blaine County. To help protect that environment, some behaviors are not allowed here.',
    },
    {
      type: 'paragraph',
      text: 'Content or activity that will not be tolerated include, but not limited to:',
    },
    { type: 'heading', level: 3, text: 'Harassment, bullying or intimidation' },
    {
      type: 'paragraph',
      text: "Treating others disrespectfully, targeting individuals, or creating an environment that feels unsafe or exclusionary is not acceptable. Respect people’s boundaries. If someone asks not to be contacted, that request must be honored.",
    },
    { type: 'heading', level: 3, text: 'Hate speech or discrimination' },
    {
      type: 'paragraph',
      text: 'Content that promotes hostility toward individuals or groups based on identity, background, or personal characteristics is not permitted.',
    },
    { type: 'heading', level: 3, text: 'Misrepresentation or impersonation' },
    {
      type: 'paragraph',
      text: 'Accounts must represent real people and real organizations. Misleading the community about identity, credentials, or affiliations is not allowed.',
    },
    { type: 'heading', level: 3, text: 'Spam or excessive promotion' },
    {
      type: 'paragraph',
      text: 'TogetherWe supports sharing opportunities and resources, but repeated promotional posting, irrelevant advertising, or automated-style activity may be removed.',
    },
    { type: 'heading', level: 3, text: 'Sharing private or sensitive information about others' },
    {
      type: 'paragraph',
      text: "Do not post someone else's story, contact information, or identifying details without permission.",
    },
    { type: 'heading', level: 3, text: 'Unsafe interactions involving minors' },
    {
      type: 'paragraph',
      text: 'Adults should not request contact information from minors or move conversations with them off-platform.',
    },
    { type: 'heading', level: 3, text: 'Medical or mental health claims presented as professional advice' },
    {
      type: 'paragraph',
      text: 'The TogetherWe Community Hub supports connection—not diagnosis or treatment. Members should not present themselves as licensed providers unless verified.',
    },
    { type: 'heading', level: 3, text: 'Content that encourages harm' },
    {
      type: 'paragraph',
      text: 'Posts that promote violence, self-harm, exploitation, or unsafe behavior are not permitted.',
    },
    { type: 'heading', level: 3, text: 'Events or groups that misrepresent their purpose' },
    {
      type: 'paragraph',
      text: 'Groups and events should clearly reflect what participants can expect.',
    },
    {
      type: 'paragraph',
      text: [
        { bold: 'Note:' },
        ' TogetherWe may remove content or accounts that do not follow these guidelines in order to protect the safety and trust of the community.',
      ],
    },
  ],
};

const termsEn: DocumentContent = {
  title: 'Terms and Conditions',
  lastUpdated: 'May 10, 2026',
  blocks: [
    {
      type: 'paragraph',
      text: 'Welcome to the TogetherWe Community Hub (the "Platform"), hosted and provided by Mental Well-Being Initiative dba TogetherWe ("TogetherWe," "we," "us"). The Platform is intended for the benefit of those who live in, work, or serve Blaine County, Idaho (collectively, "Users").',
    },
    {
      type: 'paragraph',
      text: 'This Platform exists to strengthen connection, belonging, and well-being across Blaine County, Idaho. It is a shared space—built by and for this community—where residents, organizations, and service providers can connect, collaborate, and support one another.',
    },
    {
      type: 'paragraph',
      text: 'By accessing or using the Platform, you agree to these Terms and Conditions ("Terms"). If you do not agree, please do not use the Platform.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '1. Eligibility' },
    { type: 'heading', level: 3, text: '1.1 Local Community Requirement' },
    {
      type: 'paragraph',
      text: 'This Platform is intended for individuals who live, work, or provide services in Blaine County, Idaho. By using the Platform, you affirm that this is true.',
    },
    { type: 'heading', level: 3, text: '1.2 Age Requirement' },
    {
      type: 'list',
      items: [
        'You must be at least 13 years old to use the Platform.',
        'If you are under 18, you must have permission from a parent or legal guardian.',
      ],
    },
    { type: 'heading', level: 3, text: '1.3 Account Accuracy' },
    {
      type: 'paragraph',
      text: 'You agree to provide accurate, up-to-date information about yourself and to represent yourself truthfully. You are also responsible for all activity on the Platform, whether by you or anyone authorized to access the Platform by you.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '2. Our Shared Community Commitment' },
    {
      type: 'paragraph',
      text: 'This Platform is more than a tool—it is a shared community space. We are committed to fostering a space where people feel safe to show up, connect, and belong.',
    },
    { type: 'paragraph', text: 'By participating, you agree to:' },
    {
      type: 'list',
      items: [
        'Treat others with respect and care.',
        'Contribute in ways that strengthen connection and well-being.',
        'Help maintain a safe and welcoming environment for all, especially youth.',
        'Use the Platform in a responsible manner consistent with the goals and objectives of the Platform.',
      ],
    },
    { type: 'heading', level: 2, text: '3. User Types' },
    {
      type: 'paragraph',
      text: 'The Platform supports multiple user roles. All users are subject to these Terms.',
    },
    { type: 'heading', level: 3, text: '3.1 Users' },
    {
      type: 'paragraph',
      text: 'Users, those who live in, work, or serve Blaine County, Idaho, may create profiles, join and participate in groups, attend events, and access resources.',
    },
    { type: 'heading', level: 3, text: '3.2 Groups and Organizers' },
    {
      type: 'paragraph',
      text: 'Group organizers may create and manage groups, host events, and facilitate community engagement. Organizers play an important role in maintaining safe and respectful spaces.',
    },
    { type: 'heading', level: 3, text: '3.3 Service Providers' },
    {
      type: 'paragraph',
      text: 'Local businesses, professionals, and organizations may create service pages to describe their offerings, post job listings, and interact with users if their services support mental well-being.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '4. Teen Safety & Protection Policy' },
    {
      type: 'paragraph',
      text: 'Protecting young people on this Platform is a top priority.',
    },
    { type: 'heading', level: 3, text: '4.1 Youth Participation' },
    {
      type: 'paragraph',
      text: 'We welcome teens as part of this community and have designed the Platform with safeguards in place:',
    },
    {
      type: 'list',
      items: [
        'Users ages 13–17 have limited profile features and participation on the Platform.',
        'Direct messaging is not available to minors.',
        '13–15 year-old users may not create groups.',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '4.2 Adult–Minor Interaction Boundaries',
    },
    {
      type: 'paragraph',
      text: 'To protect youth safety, adults may not:',
    },
    {
      type: 'list',
      items: [
        'Attempt to initiate private or targeted communication with minors.',
        'Request personal contact information from minors.',
        'Encourage moving conversations off the Platform.',
        'Attempt to meet with minors outside of structured, public, or supervised settings.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Any behavior that appears to cross these boundaries may result in immediate account suspension or removal.',
    },
    { type: 'heading', level: 3, text: '4.3 Off-Platform Communication' },
    { type: 'paragraph', text: 'Users may not use the Platform to:' },
    {
      type: 'list',
      items: [
        'Solicit phone numbers, social media accounts, or other contact information from minors.',
        'Encourage minors to continue communication outside the Platform.',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '4.4 Reporting Concerns Involving Youth',
    },
    {
      type: 'paragraph',
      text: 'If you see or experience anything that feels unsafe—especially involving a minor—we encourage immediate reporting. We take these concerns seriously and respond promptly.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '5. Account Responsibilities' },
    {
      type: 'list',
      items: [
        '5.1 You are responsible for maintaining the confidentiality of your login credentials.',
        '5.2 You are responsible for all activity that occurs under your account.',
        '5.3 You agree to notify us immediately of any unauthorized use of your account.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '6. Acceptable Use' },
    {
      type: 'paragraph',
      text: 'You agree to use the Platform in a respectful, lawful, and community-oriented manner. Without limitation of the foregoing, Users may NOT:',
    },
    {
      type: 'list',
      items: [
        'Post false, misleading, or deceptive information.',
        'Harass, threaten, or abuse others.',
        'Discriminate based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected characteristics.',
        'Post content that is obscene, hateful, violent, or sexually explicit.',
        'Impersonate another person or organization.',
        'Collect or misuse personal data of other users.',
        'Use the Platform for spam, scams, or unauthorized advertising.',
        'Attempt to interfere with the security or operation of the Platform.',
        'Exploit, target, or engage in inappropriate behavior toward minors under any circumstances.',
      ],
    },
    {
      type: 'paragraph',
      text: 'We reserve the right to remove content or suspend accounts that violate these rules in action or spirit immediately and without notice. We have zero tolerance for behavior that puts others, especially youth, at risk.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '7. User Content' },
    { type: 'heading', level: 3, text: '7.1 Ownership' },
    {
      type: 'paragraph',
      text: 'You retain ownership of the content you post ("User Content").',
    },
    { type: 'heading', level: 3, text: '7.2 Responsibility for Content' },
    {
      type: 'paragraph',
      text: 'You are solely responsible for your User Content and any consequences of posting it. In that regard, you represent and warrant that any User Content you post to the Platform is your own property or that you have the lawful right to post such content and that it will not infringe upon the rights of any other person or entity, and you agree to fully indemnify and hold us harmless in connection with any claim, demand, notice, or other action involving your User Content.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '8. Groups and Events' },
    { type: 'heading', level: 3, text: '8.1 Organizer Responsibilities' },
    {
      type: 'paragraph',
      text: 'Group organizers are responsible for the accuracy of group descriptions, rules, and event details. Organizers are also expected to help maintain safe, respectful environments and to address or report concerning behavior when it arises.',
    },
    { type: 'heading', level: 3, text: '8.2 Safety Expectations for Events' },
    {
      type: 'paragraph',
      text: 'The Platform does not organize, supervise, or guarantee events or meetups. Because this Platform supports real-world connection, the following expectations apply:',
    },
    {
      type: 'list',
      items: [
        'Events should be clearly described.',
        'Events involving minors should not take place in private or unsupervised environments unless hosted by a trusted organization or with appropriate parental involvement.',
        'Organizers should take reasonable steps to ensure participant safety.',
      ],
    },
    { type: 'heading', level: 3, text: '8.3 Participation in Events' },
    {
      type: 'paragraph',
      text: 'Participation in in-person events is at your own risk. While we do not supervise events, we strongly encourage safe, transparent, and inclusive participation.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '9. Service Providers, Groups, and Job Listings',
    },
    {
      type: 'heading',
      level: 3,
      text: '9.1 Job Postings by Providers and Groups',
    },
    {
      type: 'paragraph',
      text: 'Both service providers and group organizers may post job openings, internships, or volunteer opportunities on the Platform (collectively, "Postings").',
    },
    { type: 'heading', level: 3, text: '9.2 Informational Only' },
    {
      type: 'paragraph',
      text: 'All Postings are provided for informational purposes only and we shall have no responsibility for any Postings unless posted by us.',
    },
    {
      type: 'heading',
      level: 3,
      text: '9.3 No Endorsement or Employment Guarantee',
    },
    {
      type: 'paragraph',
      text: 'We do not endorse employers, groups, or job seekers, and we do not guarantee the accuracy, legality, or availability of any Posting.',
    },
    { type: 'heading', level: 3, text: '9.4 Independent Relationships' },
    {
      type: 'paragraph',
      text: 'Any employment, contracting, volunteer, or compensation arrangements formed through the Platform are solely between the posting party and the applicant. We are not a party to these relationships.',
    },
    { type: 'heading', level: 3, text: '9.5 Compliance with Laws' },
    {
      type: 'paragraph',
      text: 'Those posting jobs are responsible for complying with all applicable employment, labor, and anti-discrimination laws.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '10. Reporting, Moderation and Enforcement',
    },
    { type: 'heading', level: 3, text: '10.1 Reporting Concerns' },
    { type: 'paragraph', text: 'Users may report:' },
    {
      type: 'list',
      items: [
        'Harassment or harmful behavior.',
        'Safety concerns, especially those involving minors.',
        'Suspicious or inappropriate activity.',
        'Any activity in violation of these Terms.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Reports can be submitted via "flagging" the content for administrative review or sending an email to info@togetherwe5b.org.',
    },
    { type: 'heading', level: 3, text: '10.2 Our Response' },
    {
      type: 'paragraph',
      text: 'We review reports and may take action including:',
    },
    {
      type: 'list',
      items: [
        'Content removal.',
        'Warnings.',
        'Temporary suspension.',
        'Permanent account removal.',
        'Reporting content or activity to local enforcement that we believe may result in harm or danger to an individual or group of people.',
      ],
    },
    { type: 'heading', level: 3, text: '10.3 Moderation Approach' },
    {
      type: 'paragraph',
      text: 'We use a combination of community reporting, human review, and platform monitoring to support safety and compliance.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '11. Mental Health and Community Resources',
    },
    { type: 'heading', level: 3, text: '11.1 Informational Purposes' },
    {
      type: 'paragraph',
      text: 'Content provided on the Platform is for informational purposes only and does not constitute medical or professional advice. Users should not present themselves as licensed professionals unless verified. The Platform is intended to support connection and resource sharing—not replace professional care.',
    },
    { type: 'heading', level: 3, text: '11.2 Emergency Situations' },
    {
      type: 'paragraph',
      text: 'If you are experiencing a medical or mental health emergency, contact local emergency services immediately. The Platform does not provide emergency response.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '12. Privacy' },
    {
      type: 'paragraph',
      text: 'Your use of the Platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your information.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '13. Moderation and Enforcement' },
    {
      type: 'list',
      items: [
        '13.1 We reserve the right to monitor User Content and activity for compliance with these Terms.',
        '13.2 We may remove any content, restrict features, suspend, or terminate accounts at our discretion, with or without notice.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '14. Termination' },
    {
      type: 'paragraph',
      text: 'You may stop using the Platform at any time. We may suspend or terminate your access if you violate these Terms or if we discontinue the Platform.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '15. Disclaimers' },
    {
      type: 'paragraph',
      text: 'The Platform is provided "as is" and "as available." We make no warranties regarding availability, accuracy, or reliability of the Platform or content.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '16. Limitation of Liability' },
    {
      type: 'paragraph',
      text: 'To the maximum extent permitted by law, we will not be liable for any direct (to the extent permitted by applicable law), indirect, incidental, special, or consequential damages arising from your use of the Platform.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '17. Indemnification' },
    {
      type: 'paragraph',
      text: 'You agree to indemnify and hold harmless the Platform, its operators, and affiliates from any claims arising out of your use of the Platform or violation of these Terms.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '18. Changes to These Terms' },
    {
      type: 'paragraph',
      text: 'We may update these Terms from time to time. Continued use of the Platform after changes become effective constitutes acceptance of the revised Terms.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '19. Governing Law' },
    {
      type: 'paragraph',
      text: 'These Terms are governed by the laws of the State of Idaho, without regard to conflict of law principles.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '20. Contact Information' },
    {
      type: 'paragraph',
      text: 'If you have questions about these Terms, please contact us at TogetherWe — info@togetherwe5b.org.',
    },
  ],
};

const privacyEn: DocumentContent = {
  title: 'Privacy Policy',
  lastUpdated: 'March 23, 2026',
  blocks: [
    {
      type: 'paragraph',
      text: 'This Privacy Policy explains how Mental Well-Being Initiative dba TogetherWe ("TogetherWe," "we," "us") collects, uses, shares, and protects information about users of the TogetherWe Community Platform ("Platform") which may be accessible via the Internet or mobile application. The Platform is intended for the benefit of those who live in, work, or serve Blaine County, Idaho (collectively, "Users").',
    },
    {
      type: 'paragraph',
      text: 'This Platform is designed to support a safe, trusted, and connected community in Blaine County, Idaho. Your privacy and safety—especially for youth—are important to us.',
    },
    {
      type: 'paragraph',
      text: 'By using the Platform, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the Platform.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '1. Information We Collect' },
    { type: 'heading', level: 3, text: '1.1 Information You Provide' },
    {
      type: 'paragraph',
      text: 'We may collect information you provide directly, including:',
    },
    {
      type: 'list',
      items: [
        'Name',
        'Email address',
        'Age confirmation for 13–18 year-olds',
        'Residency confirmation in Blaine County',
        'Profile information, posts, comments, messages, and forum content',
        'Group, event, and job posting details',
        'Service provider business information',
        'Communications with us (support requests, feedback)',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '1.2 Information Collected Automatically',
    },
    {
      type: 'paragraph',
      text: 'When you use the Platform, we may automatically collect:',
    },
    {
      type: 'list',
      items: [
        'IP address',
        'Device and browser type',
        'Usage data (pages viewed, actions taken, timestamps)',
        'Precise location information',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '1.3 Cookies and Similar Technologies',
    },
    {
      type: 'paragraph',
      text: 'We may use cookies or similar technologies to:',
    },
    {
      type: 'list',
      items: [
        'Keep you logged in',
        'Remember preferences',
        'Understand how the Platform is used',
      ],
    },
    {
      type: 'paragraph',
      text: 'You can control cookies through your browser settings.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '2. How We Use Information' },
    { type: 'paragraph', text: 'We use information we collect to:' },
    {
      type: 'list',
      items: [
        'Provide and operate the Platform',
        'Verify eligibility requirements (age and county residency)',
        'Enable posts, forums, groups, events, job listings, and provider pages',
        'Facilitate communication between users',
        'Maintain safety, trust, and moderation of the Platform',
        'Detect, prevent, and respond to harmful or inappropriate behavior, including risks to minors',
        'Improve and personalize the Platform',
        'Respond to inquiries and support requests',
        'Comply with legal obligations',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '3. How Information Is Shared' },
    {
      type: 'paragraph',
      text: 'We do NOT sell or share your personal information with third parties for their marketing purposes. We may share information:',
    },
    { type: 'heading', level: 3, text: '3.1 With Other Users' },
    {
      type: 'paragraph',
      text: 'Content you post (such as profile details, posts, group activity, job listings, or provider pages) may be visible to other users of the Platform. We encourage all users—especially minors—not to share sensitive personal information publicly.',
    },
    { type: 'heading', level: 3, text: '3.2 With Service Providers' },
    {
      type: 'paragraph',
      text: 'We may share information with trusted third-party service providers who help operate the Platform (e.g., hosting, analytics, email delivery), subject to confidentiality obligations.',
    },
    { type: 'heading', level: 3, text: '3.3 For Safety and Legal Reasons' },
    {
      type: 'paragraph',
      text: 'We may disclose information when necessary to:',
    },
    {
      type: 'list',
      items: [
        'Protect the safety of users or the community',
        'Investigate or respond to violations of our Terms',
        'Address behavior that may pose a risk to minors',
        'Comply with legal obligations',
      ],
    },
    {
      type: 'paragraph',
      text: 'We may disclose information if required by law, legal process, or to protect the rights, safety, or security of users or the Platform.',
    },
    { type: 'heading', level: 3, text: '3.4 Business Transfers' },
    {
      type: 'paragraph',
      text: 'If the Platform is involved in a merger, acquisition, or asset sale, user information may be transferred as part of that transaction.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '4. Public vs. Private Information' },
    {
      type: 'list',
      items: [
        'Public content may include your display name, profile information, posts, group participation, events, job listings, and provider pages.',
        'Private information includes your email address and account credentials.',
      ],
    },
    {
      type: 'paragraph',
      text: 'We encourage users to avoid sharing sensitive personal information in public areas of the Platform.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '5. Mental Health Resources Disclaimer',
    },
    {
      type: 'paragraph',
      text: 'The Platform may include a searchable directory of recommended mental health and community resources.',
    },
    {
      type: 'list',
      items: [
        'We do not provide medical or mental health services.',
        'Inclusion of a resource does not imply endorsement.',
        'We do not collect or monitor personal health information through these listings.',
        'Users should not rely on content on the Platform as a substitute for professional care.',
      ],
    },
    {
      type: 'paragraph',
      text: 'If you are experiencing an emergency, contact local emergency services immediately.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '6. Children and Teens' },
    {
      type: 'list',
      items: [
        'The Platform is available only to users 13 years of age or older. Users under 18 must have permission from a parent or legal guardian.',
        'We do not knowingly collect personal information from children under 13. If we become aware that such information has been collected, we will delete it.',
        'We have designed the Platform with additional safeguards for users ages 13–17, including: limited profile information (name and "about me" only), no direct messaging functionality, and additional restrictions on group creation for younger users.',
        'We may monitor for and take action on behavior that appears to target or exploit minors.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '7. Data Retention' },
    {
      type: 'paragraph',
      text: 'We retain personal information only as long as necessary to:',
    },
    {
      type: 'list',
      items: [
        'Provide the Platform',
        'Comply with legal obligations',
        'Resolve disputes',
        'Enforce our Terms and Conditions',
      ],
    },
    {
      type: 'paragraph',
      text: 'You may request deletion of your account, subject to legal and operational requirements.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '8. Data Security' },
    {
      type: 'paragraph',
      text: 'We use reasonable administrative, technical, and organizational measures to protect your information. While we work to protect your data, we also rely on community members to help maintain a safe environment by reporting concerns.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '9. Your Rights and Choices' },
    { type: 'paragraph', text: 'You may have the right to:' },
    {
      type: 'list',
      items: [
        'Access your personal information',
        'Correct inaccurate information',
        'Request deletion of your account',
        'Opt out of certain communications',
      ],
    },
    {
      type: 'paragraph',
      text: 'Requests can be made by contacting us using the information below.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '10. Third-Party Links and Services' },
    {
      type: 'paragraph',
      text: 'The Platform may contain links to third-party websites or services. We are not responsible for their privacy practices.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '11. Changes to This Privacy Policy' },
    {
      type: 'paragraph',
      text: 'We may update this Privacy Policy from time to time. Continued use of the Platform after changes take effect constitutes acceptance of the revised policy.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '12. Platform Hosted in the US / Foreign Users',
    },
    {
      type: 'paragraph',
      text: 'The Platform is hosted in the United States and all information collected on and through the Platform will be processed and maintained in the United States. If you are visiting or using the Platform from outside the United States, the privacy laws of the jurisdiction in which you reside or from which you access the Platform may differ from those of the United States. By accessing or using the Platform from such other jurisdictions, you understand and unambiguously agree to your information being processed and maintained in the United States.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '13. Contact Us' },
    {
      type: 'paragraph',
      text: 'If you have questions or requests regarding this Privacy Policy, contact us at TogetherWe — info@togetherwe5b.org.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Spanish (Mexican Spanish) translations — MWBI-898.
//
// IMPORTANT: This Spanish copy is AI-generated and PENDING PROFESSIONAL LEGAL
// REVIEW before it is relied upon in production. The Terms & Conditions and
// Privacy Policy are binding/regulated legal instruments; have these reviewed
// by a qualified translator/counsel and reconcile any wording against the
// English source of record before treating them as authoritative. Terminology
// mirrors the already-reviewed acceptance modal (`modals.communityGuidelines`).
// Each block below mirrors its English counterpart one-for-one (same order,
// type, heading level, and list length); only the human-readable text differs.
// ---------------------------------------------------------------------------

const communityGuidelinesEs: DocumentContent = {
  title: 'Pautas de Participación del Centro Comunitario TogetherWe',
  lastUpdated: '26 de mayo de 2026',
  blocks: [
    {
      type: 'paragraph',
      text: '¡Bienvenido! El Centro Comunitario TogetherWe es un espacio comunitario compartido, diseñado para ayudar a las personas de todo el Condado de Blaine a establecer conexiones, encontrar apoyo y participar en lo que sucede a su alrededor a través de la vida comunitaria cotidiana.',
    },
    {
      type: 'paragraph',
      text: 'Estas pautas describen lo que significa participar con cuidado, respeto y en beneficio de la construcción de comunidad.',
    },
    { type: 'heading', level: 2, text: '1. Sé Respetuoso' },
    {
      type: 'paragraph',
      text: 'Trata a los demás con amabilidad y curiosidad, incluso cuando las perspectivas sean diferentes. Habla con tus vecinos de la misma manera en que lo harías si se encontraran cara a cara en la comunidad. El acoso, la discriminación, la intimidación y el hostigamiento no tienen cabida aquí.',
    },
    { type: 'heading', level: 2, text: '2. Sé Auténtico' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe funciona mejor cuando las personas participan con honestidad. Pedimos a todos que usen su identidad real, que se representen a sí mismos o a su organización con exactitud y que compartan información que consideren confiable. La confianza crece cuando las personas saben con quién se están conectando.',
    },
    { type: 'heading', level: 2, text: '3. Piensa en la Comunidad' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe es creado por la comunidad y para la comunidad, y existe para apoyar la participación, no solo la promoción. Las publicaciones deben estar relacionadas con el Bienestar Mental. Comparte oportunidades, invita a la colaboración y contribuye de maneras que ayuden a otros a sentirse incluidos e informados. Los grupos, las conversaciones y los eventos deben fortalecer la conexión en toda la comunidad.',
    },
    { type: 'heading', level: 2, text: '4. Ayuda a Mantener un Espacio Seguro' },
    {
      type: 'paragraph',
      text: 'Respeta los límites personales y protege la privacidad de los demás. Evita compartir información personal sensible o publicar sobre la experiencia de otra persona sin su permiso. Si algo te parece preocupante, repórtalo para que podamos ayudar a mantener el espacio acogedor para todos.',
    },
    { type: 'heading', level: 2, text: '5. Apoya la Participación Juvenil de Forma Responsable' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe da la bienvenida a la participación de adolescentes de 13 años en adelante. Los adultos deben mantener las interacciones públicas, apropiadas y respetuosas, y nunca deben solicitar información de contacto personal ni trasladar las conversaciones con menores fuera de la plataforma.',
    },
    { type: 'heading', level: 2, text: '6. Las Conversaciones sobre Salud Mental Importan Aquí' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe apoya la conexión y la experiencia compartida, pero no es una plataforma de servicios clínicos. Los miembros de la comunidad deben evitar presentarse como proveedores con licencia a menos que estén verificados. Si alguien parece estar en crisis, anímalo a comunicarse con apoyo profesional, como el 988 o los servicios de emergencia.',
    },
    { type: 'heading', level: 2, text: '7. Los Grupos Son Donde Sucede la Comunidad' },
    {
      type: 'paragraph',
      text: 'Los grupos son el corazón del Centro Comunitario TogetherWe. Los líderes de grupo ayudan a crear ambientes acogedores manteniendo las conversaciones respetuosas, los propósitos claros y las expectativas transparentes. Los grupos deben reflejar el mismo espíritu de inclusión y confianza que la plataforma en su conjunto.',
    },
    { type: 'heading', level: 2, text: '8. Si Algo No Te Parece Bien, Dilo' },
    {
      type: 'paragraph',
      text: 'Los miembros de la comunidad ayudan a mantener el Centro Comunitario seguro y acogedor al reportar comportamientos dañinos, desinformación o problemas de seguridad. Cuando sea necesario, TogetherWe puede eliminar publicaciones o cuentas que no sigan estas pautas.',
    },
    { type: 'heading', level: 2, text: '9. Lo Que No Está Permitido en el Centro Comunitario de TogetherWe' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe está diseñado para ser un espacio acogedor para la conexión y la participación en todo el Condado de Blaine. Para ayudar a proteger ese entorno, algunos comportamientos no están permitidos aquí.',
    },
    {
      type: 'paragraph',
      text: 'El contenido o las actividades que no se tolerarán incluyen, entre otros:',
    },
    { type: 'heading', level: 3, text: 'Acoso, intimidación u hostigamiento' },
    {
      type: 'paragraph',
      text: 'Tratar a los demás de manera irrespetuosa, atacar a personas o crear un entorno que se sienta inseguro o excluyente no es aceptable. Respeta los límites de las personas. Si alguien pide no ser contactado, esa solicitud debe respetarse.',
    },
    { type: 'heading', level: 3, text: 'Discurso de odio o discriminación' },
    {
      type: 'paragraph',
      text: 'No se permite el contenido que promueva la hostilidad hacia personas o grupos por su identidad, origen o características personales.',
    },
    { type: 'heading', level: 3, text: 'Tergiversación o suplantación' },
    {
      type: 'paragraph',
      text: 'Las cuentas deben representar a personas y organizaciones reales. No se permite engañar a la comunidad sobre la identidad, las credenciales o las afiliaciones.',
    },
    { type: 'heading', level: 3, text: 'Spam o promoción excesiva' },
    {
      type: 'paragraph',
      text: 'TogetherWe apoya el compartir oportunidades y recursos, pero las publicaciones promocionales repetidas, la publicidad irrelevante o la actividad de tipo automatizado pueden ser eliminadas.',
    },
    { type: 'heading', level: 3, text: 'Compartir información privada o sensible sobre otras personas' },
    {
      type: 'paragraph',
      text: 'No publiques la historia, la información de contacto ni los datos de identificación de otra persona sin su permiso.',
    },
    { type: 'heading', level: 3, text: 'Interacciones inseguras que involucran a menores' },
    {
      type: 'paragraph',
      text: 'Los adultos no deben solicitar información de contacto a menores ni trasladar las conversaciones con ellos fuera de la plataforma.',
    },
    { type: 'heading', level: 3, text: 'Afirmaciones médicas o de salud mental presentadas como asesoría profesional' },
    {
      type: 'paragraph',
      text: 'El Centro Comunitario TogetherWe apoya la conexión, no el diagnóstico ni el tratamiento. Los miembros no deben presentarse como proveedores con licencia a menos que estén verificados.',
    },
    { type: 'heading', level: 3, text: 'Contenido que fomenta el daño' },
    {
      type: 'paragraph',
      text: 'No se permiten las publicaciones que promuevan la violencia, la autolesión, la explotación o comportamientos inseguros.',
    },
    { type: 'heading', level: 3, text: 'Eventos o grupos que tergiversan su propósito' },
    {
      type: 'paragraph',
      text: 'Los grupos y eventos deben reflejar claramente lo que los participantes pueden esperar.',
    },
    {
      type: 'paragraph',
      text: [
        { bold: 'Nota:' },
        ' TogetherWe puede eliminar contenido o cuentas que no sigan estas pautas con el fin de proteger la seguridad y la confianza de la comunidad.',
      ],
    },
  ],
};

const termsEs: DocumentContent = {
  title: 'Términos y Condiciones',
  lastUpdated: '10 de mayo de 2026',
  blocks: [
    {
      type: 'paragraph',
      text: 'Bienvenido al Centro Comunitario TogetherWe (la "Plataforma"), alojado y proporcionado por Mental Well-Being Initiative que opera como TogetherWe ("TogetherWe", "nosotros"). La Plataforma está destinada al beneficio de quienes viven, trabajan o prestan servicios en el Condado de Blaine, Idaho (en conjunto, los "Usuarios").',
    },
    {
      type: 'paragraph',
      text: 'Esta Plataforma existe para fortalecer la conexión, el sentido de pertenencia y el bienestar en todo el Condado de Blaine, Idaho. Es un espacio compartido—construido por y para esta comunidad—donde los residentes, las organizaciones y los proveedores de servicios pueden conectarse, colaborar y apoyarse mutuamente.',
    },
    {
      type: 'paragraph',
      text: 'Al acceder o usar la Plataforma, aceptas estos Términos y Condiciones (los "Términos"). Si no estás de acuerdo, por favor no uses la Plataforma.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '1. Elegibilidad' },
    { type: 'heading', level: 3, text: '1.1 Requisito de Comunidad Local' },
    {
      type: 'paragraph',
      text: 'Esta Plataforma está destinada a las personas que viven, trabajan o prestan servicios en el Condado de Blaine, Idaho. Al usar la Plataforma, afirmas que esto es cierto.',
    },
    { type: 'heading', level: 3, text: '1.2 Requisito de Edad' },
    {
      type: 'list',
      items: [
        'Debes tener al menos 13 años para usar la Plataforma.',
        'Si eres menor de 18 años, debes contar con el permiso de un padre, madre o tutor legal.',
      ],
    },
    { type: 'heading', level: 3, text: '1.3 Exactitud de la Cuenta' },
    {
      type: 'paragraph',
      text: 'Aceptas proporcionar información precisa y actualizada sobre ti mismo y representarte con veracidad. También eres responsable de toda la actividad en la Plataforma, ya sea realizada por ti o por cualquier persona que autorices a acceder a la Plataforma.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '2. Nuestro Compromiso Comunitario Compartido' },
    {
      type: 'paragraph',
      text: 'Esta Plataforma es más que una herramienta: es un espacio comunitario compartido. Estamos comprometidos a fomentar un espacio donde las personas se sientan seguras de mostrarse, conectarse y pertenecer.',
    },
    { type: 'paragraph', text: 'Al participar, aceptas:' },
    {
      type: 'list',
      items: [
        'Tratar a los demás con respeto y cuidado.',
        'Contribuir de maneras que fortalezcan la conexión y el bienestar.',
        'Ayudar a mantener un entorno seguro y acogedor para todos, especialmente para los jóvenes.',
        'Usar la Plataforma de manera responsable y conforme a las metas y objetivos de la Plataforma.',
      ],
    },
    { type: 'heading', level: 2, text: '3. Tipos de Usuarios' },
    {
      type: 'paragraph',
      text: 'La Plataforma admite múltiples roles de usuario. Todos los usuarios están sujetos a estos Términos.',
    },
    { type: 'heading', level: 3, text: '3.1 Usuarios' },
    {
      type: 'paragraph',
      text: 'Los Usuarios, es decir, quienes viven, trabajan o prestan servicios en el Condado de Blaine, Idaho, pueden crear perfiles, unirse y participar en grupos, asistir a eventos y acceder a recursos.',
    },
    { type: 'heading', level: 3, text: '3.2 Grupos y Organizadores' },
    {
      type: 'paragraph',
      text: 'Los organizadores de grupos pueden crear y administrar grupos, organizar eventos y facilitar la participación comunitaria. Los organizadores desempeñan un papel importante en el mantenimiento de espacios seguros y respetuosos.',
    },
    { type: 'heading', level: 3, text: '3.3 Proveedores de Servicios' },
    {
      type: 'paragraph',
      text: 'Las empresas locales, los profesionales y las organizaciones pueden crear páginas de servicios para describir sus ofertas, publicar ofertas de empleo e interactuar con los usuarios si sus servicios apoyan el bienestar mental.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '4. Política de Seguridad y Protección de Adolescentes' },
    {
      type: 'paragraph',
      text: 'Proteger a los jóvenes en esta Plataforma es una prioridad principal.',
    },
    { type: 'heading', level: 3, text: '4.1 Participación Juvenil' },
    {
      type: 'paragraph',
      text: 'Damos la bienvenida a los adolescentes como parte de esta comunidad y hemos diseñado la Plataforma con salvaguardas en marcha:',
    },
    {
      type: 'list',
      items: [
        'Los usuarios de 13 a 17 años tienen funciones de perfil y participación limitadas en la Plataforma.',
        'Los mensajes directos no están disponibles para menores.',
        'Los usuarios de 13 a 15 años no pueden crear grupos.',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '4.2 Límites de Interacción entre Adultos y Menores',
    },
    {
      type: 'paragraph',
      text: 'Para proteger la seguridad de los jóvenes, los adultos no pueden:',
    },
    {
      type: 'list',
      items: [
        'Intentar iniciar comunicación privada o dirigida con menores.',
        'Solicitar información de contacto personal a menores.',
        'Fomentar el traslado de conversaciones fuera de la Plataforma.',
        'Intentar reunirse con menores fuera de entornos estructurados, públicos o supervisados.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Cualquier comportamiento que parezca cruzar estos límites puede resultar en la suspensión o eliminación inmediata de la cuenta.',
    },
    { type: 'heading', level: 3, text: '4.3 Comunicación Fuera de la Plataforma' },
    { type: 'paragraph', text: 'Los usuarios no pueden usar la Plataforma para:' },
    {
      type: 'list',
      items: [
        'Solicitar números de teléfono, cuentas de redes sociales u otra información de contacto a menores.',
        'Animar a los menores a continuar la comunicación fuera de la Plataforma.',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '4.4 Reportar Inquietudes que Involucren a Jóvenes',
    },
    {
      type: 'paragraph',
      text: 'Si ves o experimentas algo que te parezca inseguro—especialmente si involucra a un menor—te animamos a reportarlo de inmediato. Tomamos estas inquietudes con seriedad y respondemos con prontitud.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '5. Responsabilidades de la Cuenta' },
    {
      type: 'list',
      items: [
        '5.1 Eres responsable de mantener la confidencialidad de tus credenciales de inicio de sesión.',
        '5.2 Eres responsable de toda la actividad que ocurra en tu cuenta.',
        '5.3 Aceptas notificarnos de inmediato sobre cualquier uso no autorizado de tu cuenta.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '6. Uso Aceptable' },
    {
      type: 'paragraph',
      text: 'Aceptas usar la Plataforma de manera respetuosa, legal y orientada a la comunidad. Sin limitación de lo anterior, los Usuarios NO pueden:',
    },
    {
      type: 'list',
      items: [
        'Publicar información falsa, engañosa o fraudulenta.',
        'Acosar, amenazar o abusar de los demás.',
        'Discriminar por motivos de raza, etnia, religión, género, orientación sexual, discapacidad u otras características protegidas.',
        'Publicar contenido obsceno, de odio, violento o sexualmente explícito.',
        'Hacerse pasar por otra persona u organización.',
        'Recopilar o hacer mal uso de los datos personales de otros usuarios.',
        'Usar la Plataforma para spam, estafas o publicidad no autorizada.',
        'Intentar interferir con la seguridad o el funcionamiento de la Plataforma.',
        'Explotar, atacar o tener un comportamiento inapropiado hacia menores bajo ninguna circunstancia.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Nos reservamos el derecho de eliminar contenido o suspender cuentas que violen estas reglas, en acción o en espíritu, de manera inmediata y sin previo aviso. Tenemos cero tolerancia hacia el comportamiento que ponga en riesgo a otros, especialmente a los jóvenes.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '7. Contenido del Usuario' },
    { type: 'heading', level: 3, text: '7.1 Propiedad' },
    {
      type: 'paragraph',
      text: 'Conservas la propiedad del contenido que publicas (el "Contenido del Usuario").',
    },
    { type: 'heading', level: 3, text: '7.2 Responsabilidad por el Contenido' },
    {
      type: 'paragraph',
      text: 'Eres el único responsable de tu Contenido del Usuario y de cualquier consecuencia de publicarlo. En ese sentido, declaras y garantizas que cualquier Contenido del Usuario que publiques en la Plataforma es de tu propiedad o que tienes el derecho legal de publicar dicho contenido y que no infringirá los derechos de ninguna otra persona o entidad, y aceptas indemnizarnos y eximirnos de responsabilidad por completo en relación con cualquier reclamación, demanda, notificación u otra acción que involucre tu Contenido del Usuario.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '8. Grupos y Eventos' },
    { type: 'heading', level: 3, text: '8.1 Responsabilidades del Organizador' },
    {
      type: 'paragraph',
      text: 'Los organizadores de grupos son responsables de la exactitud de las descripciones de los grupos, las reglas y los detalles de los eventos. También se espera que los organizadores ayuden a mantener entornos seguros y respetuosos y que aborden o reporten comportamientos preocupantes cuando surjan.',
    },
    { type: 'heading', level: 3, text: '8.2 Expectativas de Seguridad para los Eventos' },
    {
      type: 'paragraph',
      text: 'La Plataforma no organiza, supervisa ni garantiza los eventos o encuentros. Debido a que esta Plataforma apoya la conexión en el mundo real, se aplican las siguientes expectativas:',
    },
    {
      type: 'list',
      items: [
        'Los eventos deben describirse con claridad.',
        'Los eventos que involucren a menores no deben llevarse a cabo en entornos privados o sin supervisión, a menos que sean organizados por una organización de confianza o con la participación adecuada de los padres.',
        'Los organizadores deben tomar medidas razonables para garantizar la seguridad de los participantes.',
      ],
    },
    { type: 'heading', level: 3, text: '8.3 Participación en Eventos' },
    {
      type: 'paragraph',
      text: 'La participación en eventos presenciales es bajo tu propio riesgo. Aunque no supervisamos los eventos, recomendamos encarecidamente una participación segura, transparente e inclusiva.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '9. Proveedores de Servicios, Grupos y Ofertas de Empleo',
    },
    {
      type: 'heading',
      level: 3,
      text: '9.1 Publicaciones de Empleo por Proveedores y Grupos',
    },
    {
      type: 'paragraph',
      text: 'Tanto los proveedores de servicios como los organizadores de grupos pueden publicar vacantes de empleo, prácticas u oportunidades de voluntariado en la Plataforma (en conjunto, las "Publicaciones").',
    },
    { type: 'heading', level: 3, text: '9.2 Solo Informativo' },
    {
      type: 'paragraph',
      text: 'Todas las Publicaciones se proporcionan únicamente con fines informativos y no tendremos ninguna responsabilidad por las Publicaciones a menos que hayan sido publicadas por nosotros.',
    },
    {
      type: 'heading',
      level: 3,
      text: '9.3 Sin Aval ni Garantía de Empleo',
    },
    {
      type: 'paragraph',
      text: 'No avalamos a los empleadores, grupos o solicitantes de empleo, y no garantizamos la exactitud, legalidad o disponibilidad de ninguna Publicación.',
    },
    { type: 'heading', level: 3, text: '9.4 Relaciones Independientes' },
    {
      type: 'paragraph',
      text: 'Cualquier acuerdo de empleo, contratación, voluntariado o compensación formado a través de la Plataforma es únicamente entre la parte que publica y el solicitante. No somos parte de estas relaciones.',
    },
    { type: 'heading', level: 3, text: '9.5 Cumplimiento de las Leyes' },
    {
      type: 'paragraph',
      text: 'Quienes publican empleos son responsables de cumplir con todas las leyes aplicables en materia de empleo, laborales y antidiscriminación.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '10. Reportes, Moderación y Cumplimiento',
    },
    { type: 'heading', level: 3, text: '10.1 Reportar Inquietudes' },
    { type: 'paragraph', text: 'Los usuarios pueden reportar:' },
    {
      type: 'list',
      items: [
        'Acoso o comportamiento dañino.',
        'Problemas de seguridad, especialmente los que involucran a menores.',
        'Actividad sospechosa o inapropiada.',
        'Cualquier actividad que infrinja estos Términos.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Los reportes pueden enviarse "reportando" el contenido para su revisión administrativa o enviando un correo electrónico a info@togetherwe5b.org.',
    },
    { type: 'heading', level: 3, text: '10.2 Nuestra Respuesta' },
    {
      type: 'paragraph',
      text: 'Revisamos los reportes y podemos tomar medidas que incluyen:',
    },
    {
      type: 'list',
      items: [
        'Eliminación de contenido.',
        'Advertencias.',
        'Suspensión temporal.',
        'Eliminación permanente de la cuenta.',
        'Reportar a las autoridades locales el contenido o la actividad que creamos que pueda resultar en daño o peligro para una persona o un grupo de personas.',
      ],
    },
    { type: 'heading', level: 3, text: '10.3 Enfoque de Moderación' },
    {
      type: 'paragraph',
      text: 'Utilizamos una combinación de reportes de la comunidad, revisión humana y monitoreo de la plataforma para apoyar la seguridad y el cumplimiento.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '11. Salud Mental y Recursos Comunitarios',
    },
    { type: 'heading', level: 3, text: '11.1 Fines Informativos' },
    {
      type: 'paragraph',
      text: 'El contenido proporcionado en la Plataforma es únicamente con fines informativos y no constituye asesoría médica o profesional. Los usuarios no deben presentarse como profesionales con licencia a menos que estén verificados. La Plataforma está destinada a apoyar la conexión y el intercambio de recursos, no a reemplazar la atención profesional.',
    },
    { type: 'heading', level: 3, text: '11.2 Situaciones de Emergencia' },
    {
      type: 'paragraph',
      text: 'Si estás experimentando una emergencia médica o de salud mental, comunícate de inmediato con los servicios de emergencia locales. La Plataforma no proporciona respuesta de emergencia.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '12. Privacidad' },
    {
      type: 'paragraph',
      text: 'Tu uso de la Plataforma también se rige por nuestra Política de Privacidad, que explica cómo recopilamos, usamos y protegemos tu información.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '13. Moderación y Cumplimiento' },
    {
      type: 'list',
      items: [
        '13.1 Nos reservamos el derecho de monitorear el Contenido del Usuario y la actividad para verificar el cumplimiento de estos Términos.',
        '13.2 Podemos eliminar cualquier contenido, restringir funciones, suspender o cancelar cuentas a nuestra discreción, con o sin previo aviso.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '14. Terminación' },
    {
      type: 'paragraph',
      text: 'Puedes dejar de usar la Plataforma en cualquier momento. Podemos suspender o cancelar tu acceso si violas estos Términos o si descontinuamos la Plataforma.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '15. Renuncias de Responsabilidad' },
    {
      type: 'paragraph',
      text: 'La Plataforma se proporciona "tal cual" y "según disponibilidad". No ofrecemos garantías con respecto a la disponibilidad, exactitud o confiabilidad de la Plataforma o su contenido.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '16. Limitación de Responsabilidad' },
    {
      type: 'paragraph',
      text: 'En la máxima medida permitida por la ley, no seremos responsables de ningún daño directo (en la medida en que lo permita la ley aplicable), indirecto, incidental, especial o consecuente que surja de tu uso de la Plataforma.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '17. Indemnización' },
    {
      type: 'paragraph',
      text: 'Aceptas indemnizar y eximir de responsabilidad a la Plataforma, sus operadores y afiliados de cualquier reclamación que surja de tu uso de la Plataforma o de la violación de estos Términos.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '18. Cambios a Estos Términos' },
    {
      type: 'paragraph',
      text: 'Podemos actualizar estos Términos de vez en cuando. El uso continuo de la Plataforma después de que los cambios entren en vigor constituye la aceptación de los Términos revisados.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '19. Ley Aplicable' },
    {
      type: 'paragraph',
      text: 'Estos Términos se rigen por las leyes del Estado de Idaho, sin tener en cuenta los principios de conflicto de leyes.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '20. Información de Contacto' },
    {
      type: 'paragraph',
      text: 'Si tienes preguntas sobre estos Términos, contáctanos en TogetherWe — info@togetherwe5b.org.',
    },
  ],
};

const privacyEs: DocumentContent = {
  title: 'Política de Privacidad',
  lastUpdated: '23 de marzo de 2026',
  blocks: [
    {
      type: 'paragraph',
      text: 'Esta Política de Privacidad explica cómo Mental Well-Being Initiative que opera como TogetherWe ("TogetherWe", "nosotros") recopila, usa, comparte y protege la información sobre los usuarios de la Plataforma Comunitaria TogetherWe (la "Plataforma"), a la que se puede acceder a través de Internet o de una aplicación móvil. La Plataforma está destinada al beneficio de quienes viven, trabajan o prestan servicios en el Condado de Blaine, Idaho (en conjunto, los "Usuarios").',
    },
    {
      type: 'paragraph',
      text: 'Esta Plataforma está diseñada para apoyar una comunidad segura, confiable y conectada en el Condado de Blaine, Idaho. Tu privacidad y seguridad—especialmente la de los jóvenes—son importantes para nosotros.',
    },
    {
      type: 'paragraph',
      text: 'Al usar la Plataforma, aceptas las prácticas descritas en esta Política de Privacidad. Si no estás de acuerdo, por favor no uses la Plataforma.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '1. Información que Recopilamos' },
    { type: 'heading', level: 3, text: '1.1 Información que Proporcionas' },
    {
      type: 'paragraph',
      text: 'Podemos recopilar la información que proporcionas directamente, incluyendo:',
    },
    {
      type: 'list',
      items: [
        'Nombre',
        'Dirección de correo electrónico',
        'Confirmación de edad para personas de 13 a 18 años',
        'Confirmación de residencia en el Condado de Blaine',
        'Información del perfil, publicaciones, comentarios, mensajes y contenido del foro',
        'Detalles de grupos, eventos y ofertas de empleo',
        'Información comercial de los proveedores de servicios',
        'Comunicaciones con nosotros (solicitudes de soporte, comentarios)',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '1.2 Información Recopilada Automáticamente',
    },
    {
      type: 'paragraph',
      text: 'Cuando usas la Plataforma, podemos recopilar automáticamente:',
    },
    {
      type: 'list',
      items: [
        'Dirección IP',
        'Tipo de dispositivo y navegador',
        'Datos de uso (páginas vistas, acciones realizadas, marcas de tiempo)',
        'Información de ubicación precisa',
      ],
    },
    {
      type: 'heading',
      level: 3,
      text: '1.3 Cookies y Tecnologías Similares',
    },
    {
      type: 'paragraph',
      text: 'Podemos usar cookies o tecnologías similares para:',
    },
    {
      type: 'list',
      items: [
        'Mantener tu sesión iniciada',
        'Recordar preferencias',
        'Entender cómo se usa la Plataforma',
      ],
    },
    {
      type: 'paragraph',
      text: 'Puedes controlar las cookies a través de la configuración de tu navegador.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '2. Cómo Usamos la Información' },
    { type: 'paragraph', text: 'Usamos la información que recopilamos para:' },
    {
      type: 'list',
      items: [
        'Proporcionar y operar la Plataforma',
        'Verificar los requisitos de elegibilidad (edad y residencia en el condado)',
        'Habilitar publicaciones, foros, grupos, eventos, ofertas de empleo y páginas de proveedores',
        'Facilitar la comunicación entre usuarios',
        'Mantener la seguridad, la confianza y la moderación de la Plataforma',
        'Detectar, prevenir y responder a comportamientos dañinos o inapropiados, incluyendo riesgos para los menores',
        'Mejorar y personalizar la Plataforma',
        'Responder a consultas y solicitudes de soporte',
        'Cumplir con las obligaciones legales',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '3. Cómo se Comparte la Información' },
    {
      type: 'paragraph',
      text: 'NO vendemos ni compartimos tu información personal con terceros para sus fines de marketing. Podemos compartir información:',
    },
    { type: 'heading', level: 3, text: '3.1 Con Otros Usuarios' },
    {
      type: 'paragraph',
      text: 'El contenido que publicas (como los detalles del perfil, las publicaciones, la actividad en grupos, las ofertas de empleo o las páginas de proveedores) puede ser visible para otros usuarios de la Plataforma. Animamos a todos los usuarios—especialmente a los menores—a no compartir información personal sensible públicamente.',
    },
    { type: 'heading', level: 3, text: '3.2 Con Proveedores de Servicios' },
    {
      type: 'paragraph',
      text: 'Podemos compartir información con proveedores de servicios externos de confianza que ayudan a operar la Plataforma (por ejemplo, alojamiento, análisis, envío de correos electrónicos), sujetos a obligaciones de confidencialidad.',
    },
    { type: 'heading', level: 3, text: '3.3 Por Razones de Seguridad y Legales' },
    {
      type: 'paragraph',
      text: 'Podemos divulgar información cuando sea necesario para:',
    },
    {
      type: 'list',
      items: [
        'Proteger la seguridad de los usuarios o de la comunidad',
        'Investigar o responder a violaciones de nuestros Términos',
        'Abordar comportamientos que puedan representar un riesgo para los menores',
        'Cumplir con las obligaciones legales',
      ],
    },
    {
      type: 'paragraph',
      text: 'Podemos divulgar información si así lo exige la ley, un proceso legal, o para proteger los derechos, la seguridad o la protección de los usuarios o de la Plataforma.',
    },
    { type: 'heading', level: 3, text: '3.4 Transferencias Comerciales' },
    {
      type: 'paragraph',
      text: 'Si la Plataforma participa en una fusión, adquisición o venta de activos, la información de los usuarios puede transferirse como parte de esa transacción.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '4. Información Pública vs. Privada' },
    {
      type: 'list',
      items: [
        'El contenido público puede incluir tu nombre para mostrar, la información de tu perfil, las publicaciones, la participación en grupos, los eventos, las ofertas de empleo y las páginas de proveedores.',
        'La información privada incluye tu dirección de correo electrónico y las credenciales de tu cuenta.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Animamos a los usuarios a evitar compartir información personal sensible en las áreas públicas de la Plataforma.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '5. Aviso sobre los Recursos de Salud Mental',
    },
    {
      type: 'paragraph',
      text: 'La Plataforma puede incluir un directorio con función de búsqueda de recursos recomendados de salud mental y comunitarios.',
    },
    {
      type: 'list',
      items: [
        'No proporcionamos servicios médicos ni de salud mental.',
        'La inclusión de un recurso no implica respaldo.',
        'No recopilamos ni monitoreamos información de salud personal a través de estos listados.',
        'Los usuarios no deben basarse en el contenido de la Plataforma como sustituto de la atención profesional.',
      ],
    },
    {
      type: 'paragraph',
      text: 'Si estás experimentando una emergencia, comunícate de inmediato con los servicios de emergencia locales.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '6. Niños y Adolescentes' },
    {
      type: 'list',
      items: [
        'La Plataforma está disponible únicamente para usuarios de 13 años de edad o más. Los usuarios menores de 18 años deben contar con el permiso de un padre, madre o tutor legal.',
        'No recopilamos a sabiendas información personal de niños menores de 13 años. Si llegamos a tener conocimiento de que se ha recopilado dicha información, la eliminaremos.',
        'Hemos diseñado la Plataforma con salvaguardas adicionales para los usuarios de 13 a 17 años, incluyendo: información de perfil limitada (solo nombre y "sobre mí"), sin función de mensajería directa y restricciones adicionales en la creación de grupos para los usuarios más jóvenes.',
        'Podemos monitorear y tomar medidas sobre comportamientos que parezcan dirigirse a menores o explotarlos.',
      ],
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '7. Retención de Datos' },
    {
      type: 'paragraph',
      text: 'Conservamos la información personal solo durante el tiempo necesario para:',
    },
    {
      type: 'list',
      items: [
        'Proporcionar la Plataforma',
        'Cumplir con las obligaciones legales',
        'Resolver disputas',
        'Hacer cumplir nuestros Términos y Condiciones',
      ],
    },
    {
      type: 'paragraph',
      text: 'Puedes solicitar la eliminación de tu cuenta, sujeto a los requisitos legales y operativos.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '8. Seguridad de los Datos' },
    {
      type: 'paragraph',
      text: 'Utilizamos medidas administrativas, técnicas y organizativas razonables para proteger tu información. Si bien trabajamos para proteger tus datos, también dependemos de los miembros de la comunidad para ayudar a mantener un entorno seguro reportando inquietudes.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '9. Tus Derechos y Opciones' },
    { type: 'paragraph', text: 'Es posible que tengas derecho a:' },
    {
      type: 'list',
      items: [
        'Acceder a tu información personal',
        'Corregir información inexacta',
        'Solicitar la eliminación de tu cuenta',
        'Optar por no recibir ciertas comunicaciones',
      ],
    },
    {
      type: 'paragraph',
      text: 'Las solicitudes pueden hacerse contactándonos mediante la información que se proporciona a continuación.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '10. Enlaces y Servicios de Terceros' },
    {
      type: 'paragraph',
      text: 'La Plataforma puede contener enlaces a sitios web o servicios de terceros. No somos responsables de sus prácticas de privacidad.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '11. Cambios a Esta Política de Privacidad' },
    {
      type: 'paragraph',
      text: 'Podemos actualizar esta Política de Privacidad de vez en cuando. El uso continuo de la Plataforma después de que los cambios entren en vigor constituye la aceptación de la política revisada.',
    },
    { type: 'divider' },
    {
      type: 'heading',
      level: 2,
      text: '12. Plataforma Alojada en EE. UU. / Usuarios Extranjeros',
    },
    {
      type: 'paragraph',
      text: 'La Plataforma está alojada en los Estados Unidos y toda la información recopilada en y a través de la Plataforma será procesada y mantenida en los Estados Unidos. Si estás visitando o usando la Plataforma desde fuera de los Estados Unidos, las leyes de privacidad de la jurisdicción en la que resides o desde la cual accedes a la Plataforma pueden diferir de las de los Estados Unidos. Al acceder o usar la Plataforma desde dichas otras jurisdicciones, entiendes y aceptas de manera inequívoca que tu información sea procesada y mantenida en los Estados Unidos.',
    },
    { type: 'divider' },
    { type: 'heading', level: 2, text: '13. Contáctanos' },
    {
      type: 'paragraph',
      text: 'Si tienes preguntas o solicitudes con respecto a esta Política de Privacidad, contáctanos en TogetherWe — info@togetherwe5b.org.',
    },
  ],
};

export const guidelinesLegalContent = {
  en: {
    communityGuidelines: communityGuidelinesEn,
    terms: termsEn,
    privacy: privacyEn,
  },
  // Mexican Spanish translations (MWBI-898). AI-generated and PENDING
  // PROFESSIONAL LEGAL REVIEW before production — see the note above the
  // `*Es` definitions. Each `es` document mirrors its `en` counterpart's block
  // structure; the page (page.tsx) renders these unchanged.
  es: {
    communityGuidelines: communityGuidelinesEs,
    terms: termsEs,
    privacy: privacyEs,
  },
} as const;
